import React, { useState, useMemo } from "react";
import {
  Box,
  useTheme,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Alert,
} from "@mui/material";
import { TABLE_HEAD_BOXED_POSITIONS } from "../../component/Methods";
import { filterAndPrepareBoxedPositions } from "./boxedPositionsUtils";
import { EXPAND_COL_WIDTH, getTableSx } from "./boxedPositionsTableStyles";
import BoxedPositionsTableToolbar from "./BoxedPositionsTableToolbar";
import BoxedPositionGroupRows from "./BoxedPositionGroupRows";

const PositionsTable = ({
  originalPositions,
  funds,
  strategies,
  custodians,
}) => {
  const theme = useTheme();
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(1000);
  const [openRowIds, setOpenRowIds] = useState(() => new Set());
  const [sortField, setSortField] = useState("tickerSymbol");
  const [sortDirection, setSortDirection] = useState("asc");
  const [error, setError] = useState(null);

  const filteredPositions = useMemo(
    () =>
      filterAndPrepareBoxedPositions({
        originalPositions,
        search,
        sortField,
        sortDirection,
        funds,
        custodians,
        strategies,
      }),
    [
      originalPositions,
      search,
      sortField,
      sortDirection,
      funds,
      custodians,
      strategies,
    ]
  );

  const toggleRow = (rowId) => {
    try {
      setOpenRowIds((prev) => {
        const next = new Set(prev);
        if (next.has(rowId)) next.delete(rowId);
        else next.add(rowId);
        return next;
      });
    } catch (err) {
      setError("Error toggling row expansion");
    }
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
    setPage(0);
  };

  const colCount = TABLE_HEAD_BOXED_POSITIONS.length + 1;



  return (
    <Box sx={{ width: "100%" }}>
      <BoxedPositionsTableToolbar
        theme={theme}
        search={search}
        onSearchChange={(value) => {
          setSearch(value);
          setPage(0);
        }}
        positionCount={filteredPositions.length}
      />

      <TableContainer
        sx={{ height: "calc(100vh - 80px)", overflowY: "scroll" }}
      >
        <Table stickyHeader size="small" sx={getTableSx(theme)}>
          <colgroup>
            <col style={{ width: EXPAND_COL_WIDTH }} />
            {TABLE_HEAD_BOXED_POSITIONS.map((headCell) => (
              <col key={headCell.id} />
            ))}
          </colgroup>
          <TableHead>
            <TableRow>
              <TableCell
                sx={{
                  width: EXPAND_COL_WIDTH,
                  backgroundColor: theme.palette.grey[100],
                  borderBottom: `2px solid ${theme.palette.divider}`,
                  borderColor: theme.palette.divider,
                }}
              />
              {TABLE_HEAD_BOXED_POSITIONS.map((headCell) => (
                <TableCell
                  key={headCell.id}
                  align={headCell.alignLeft ? "left" : "right"}
                  onClick={() => handleSort(headCell.id)}
                  sx={{
                    fontWeight: 600,
                    backgroundColor: theme.palette.grey[100],
                    color: theme.palette.text.primary,
                    borderBottom: `2px solid ${theme.palette.divider}`,
                    cursor: "pointer",
                    userSelect: "none",
                    whiteSpace: "nowrap",
                    "&:hover": {
                      backgroundColor: theme.palette.grey[200],
                    },
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: headCell.alignLeft
                        ? "flex-start"
                        : "flex-end",
                    }}
                  >
                    {headCell.label}
                    {sortField === headCell.id && (
                      <Box
                        component="span"
                        sx={{
                          ml: 0.5,
                          fontSize: "12px",
                          color: theme.palette.primary.main,
                          fontWeight: "bold",
                        }}
                      >
                        {sortDirection === "asc" ? "↑" : "↓"}
                      </Box>
                    )}
                  </Box>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {!filteredPositions || !Array.isArray(filteredPositions) ? (
              <TableRow>
                <TableCell colSpan={colCount} align="center">
                  <Alert severity="error">No data available</Alert>
                </TableCell>
              </TableRow>
            ) : (
              <>
                {error && (
                  <TableRow>
                    <TableCell colSpan={colCount} align="center">
                      <Alert severity="error">{error}</Alert>
                    </TableCell>
                  </TableRow>
                )}
                {filteredPositions
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((row, index) => {
                    if (!row) return null;
                    return (
                      <BoxedPositionGroupRows
                        key={row.id || index}
                        row={row}
                        theme={theme}
                        isOpen={openRowIds.has(row.id)}
                        onToggle={toggleRow}
                        funds={funds}
                        strategies={strategies}
                        custodians={custodians}
                      />
                    );
                  })}
              </>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        rowsPerPageOptions={[25, 50, 100, 500, 1000]}
        component="div"
        count={filteredPositions.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={(_, newPage) => setPage(newPage)}
        onRowsPerPageChange={(event) => {
          setRowsPerPage(parseInt(event.target.value, 10));
          setPage(0);
        }}
        sx={{
          borderTop: `1px solid ${theme.palette.divider}`,
          backgroundColor: theme.palette.grey[100],
        }}
      />
    </Box>
  );
};

export default PositionsTable;
